"use client";

import React, { useMemo } from "react";
import NextLink from "next/link";
import { useLocale, useTranslations } from "next-intl";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Drawer,
  IconButton,
  Link,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import FinishPicker from "src/components/canvas/FinishPicker";
import OptionCard from "src/components/canvas/OptionCard";
import { CompactHeading } from "src/components/canvas/CompactOptions";
import { finishChoices } from "src/components/canvas/finish-options";
import SizePicker from "src/components/canvas/SizePicker";
import TrustStrip from "src/components/canvas/TrustStrip";
import { trustPointKeys } from "src/components/canvas/trust-points";
import { formatMoney } from "src/components/canvas/variant-price";
import { artworkImageUrl } from "./useArtwork";
import { artworkAspect, localized, type Artwork } from "./artwork-types";
import { useArtworkOptions } from "./useArtworkOptions";
import { useMaterialLabel } from "./useCardOffers";
import { materialOffers } from "./artwork-purchase";
import { tileSx } from "./ArtworkCard";
import HideChatWidget from "./HideChatWidget";

export interface QuickAddSheetProps {
  /** The work being added; null closes the sheet. */
  artwork: Artwork | null;
  onClose: () => void;
  /** The work's own page, for "Več o delu". */
  hrefOf: (artwork: Artwork) => string;
  /** The page's add-to-cart (useAddArtworkToCart); resolves true on success. */
  onAdd: (slug: string, variantId: string) => Promise<boolean>;
  adding: boolean;
  error: string | null;
}

/**
 * Finish and size for a work without leaving the list.
 *
 * The card's "od X €" is a price for one size of one finish, so a basket
 * button on the card cannot add anything by itself. This asks the material
 * first — two large cards, each saying what it is and what it costs from —
 * then the frame or surface and the size with the canvas editor's own
 * compact pickers, offering only what this master can be printed as. A bottom sheet on a phone, where the thumb is; a small dialog on a
 * desktop.
 */
export default function QuickAddSheet(props: QuickAddSheetProps) {
  const { artwork, onClose } = props;
  const desktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), { noSsr: true });
  const open = !!artwork;
  // Keyed by the work, so the next one opens on its own default size rather
  // than on whatever was picked for the last.
  const body = artwork ? <QuickAddBody key={artwork.id} {...props} artwork={artwork} /> : null;

  return (
    <>
      {open && <HideChatWidget />}
      {desktop ? (
        <Dialog
          open={open}
          onClose={onClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          {body}
        </Dialog>
      ) : (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: "92vh",
            },
          }}
        >
          {body}
        </Drawer>
      )}
    </>
  );
}

function QuickAddBody({
  artwork,
  onClose,
  hrefOf,
  onAdd,
  adding,
  error,
}: QuickAddSheetProps & { artwork: Artwork }) {
  const t = useTranslations("art");
  const tEditor = useTranslations("editor");
  const te = tEditor as unknown as (key: string) => string;
  const locale = useLocale();
  const {
    variants,
    axes,
    printable,
    printableVariants,
    selected,
    select,
    finish,
    changeFinishTo,
    groups,
    currency,
    price,
  } = useArtworkOptions(artwork);

  const materialLabel = useMaterialLabel(axes);
  const materials = useMemo(
    () => finishChoices(printableVariants, axes, finish, "material"),
    [printableVariants, axes, finish],
  );
  const offers = useMemo(() => materialOffers(printable, currency), [printable, currency]);

  const title = localized(artwork.title, locale);
  const aspect = artworkAspect(artwork);
  const material = selected?.variant.axisValues?.material;
  const mount = selected?.variant.axisValues?.mount;
  const headingId = `quick-add-${artwork.id}`;

  const handleAdd = async () => {
    if (!selected) return;
    const ok = await onAdd(artwork.slug, selected.variant.id);
    if (ok) onClose();
  };

  return (
    <Box
      role="region"
      aria-labelledby={headingId}
      sx={{ display: "flex", flexDirection: "column", minHeight: 0, maxHeight: { xs: "92vh", md: "86vh" } }}
    >
      <Box sx={{ overflowY: "auto", px: { xs: 2, md: 3 }, pt: { xs: 1.25, md: 3 }, pb: 2 }}>
        <Box
          aria-hidden
          sx={{
            display: { xs: "block", md: "none" },
            width: 40,
            height: 4,
            borderRadius: 2,
            bgcolor: "divider",
            mx: "auto",
            mb: 1.5,
          }}
        />
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={(theme) => ({
              ...tileSx(theme),
              width: { xs: 72, md: 88 },
              aspectRatio: "4 / 5",
              borderRadius: 1,
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 1,
            })}
          >
            <Box
              component="img"
              src={artworkImageUrl(artwork.slug, "thumb", artwork.updatedAt)}
              alt=""
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "100%",
                aspectRatio: String(aspect || 1),
                boxShadow: "0 6px 12px -6px rgba(20,24,32,.5)",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ letterSpacing: ".07em", textTransform: "uppercase" }}
            >
              {artwork.artist}
            </Typography>
            <Typography id={headingId} variant="h6" component="h2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Link
              component={NextLink}
              href={hrefOf(artwork)}
              onClick={onClose}
              sx={{ display: "inline-block", mt: 0.75, fontSize: 14, fontWeight: 600 }}
            >
              {t("moreAboutWork")} →
            </Link>
          </Box>
          <IconButton onClick={onClose} aria-label={t("close")} sx={{ mt: -0.5, mr: -1 }}>
            <CloseIcon />
          </IconButton>
        </Stack>

        <Box sx={{ mt: 2.5 }}>
          {variants === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : printable.length === 0 ? (
            <Alert severity="info">{t("noSizesForWork")}</Alert>
          ) : (
            <Stack spacing={2.25}>
              {/* The material first and big: it is the decision that changes
                  what arrives, and the one the card's two prices lead to. */}
              {materials.length > 1 && (
                <Box>
                  <CompactHeading id={`${headingId}-material`}>{te("canvasMaterial")}</CompactHeading>
                  <Box
                    role="radiogroup"
                    aria-labelledby={`${headingId}-material`}
                    sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25 }}
                  >
                    {materials.map((material) => {
                      const hintKey = `canvasMaterial_${material}_hint`;
                      const offer = offers.find((o) => o.material === material);
                      return (
                        <OptionCard
                          key={material}
                          selected={finish.material === material}
                          onClick={() => changeFinishTo("material", material)}
                          title={materialLabel(material)}
                          subtitle={tEditor.has(hintKey) ? tEditor(hintKey) : undefined}
                          price={
                            offer
                              ? t("fromPrice", {
                                  price: formatMoney(offer.price.amount, offer.price.currency, locale),
                                })
                              : undefined
                          }
                        />
                      );
                    })}
                  </Box>
                </Box>
              )}
              <FinishPicker
                variants={printableVariants}
                axes={axes}
                finish={finish}
                locale={locale}
                onChange={changeFinishTo}
                t={te}
                compact
                only={["mount", "surface"]}
              />
              <SizePicker
                groups={groups}
                selectedId={selected?.variant.id ?? null}
                currency={currency}
                locale={locale}
                axes={axes}
                orientation={aspect >= 1 ? "landscape" : "portrait"}
                onOrientationChange={() => undefined}
                onSelect={(v) => select(v.id)}
                canRotate={false}
                t={te}
                compact
              />
            </Stack>
          )}
        </Box>
      </Box>

      {printable.length > 0 && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            px: { xs: 2, md: 3 },
            pt: 1.5,
            pb: { xs: "calc(12px + env(safe-area-inset-bottom))", md: 2.5 },
            bgcolor: "background.paper",
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flexShrink: 0 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.1, whiteSpace: "nowrap" }}>
                {price ? formatMoney(price.amount, price.currency, locale) : "—"}
              </Typography>
              {price?.compareAt != null && price.compareAt > price.amount && (
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: 14, textDecoration: "line-through" }}
                >
                  {formatMoney(price.compareAt, price.currency, locale)}
                </Typography>
              )}
            </Box>
            <Button
              variant="contained"
              size="large"
              disabled={adding || !selected}
              onClick={handleAdd}
              startIcon={adding ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ flex: 1, minWidth: 0, minHeight: 50, fontWeight: 700, textTransform: "none", fontSize: 16 }}
            >
              {t("addToCart")}
            </Button>
          </Stack>
          <Box sx={{ mt: 1.25 }}>
            <TrustStrip lines={trustPointKeys(material, mount).map((key) => te(key))} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
